import {
  defaultApiEnvForThisEnv,
  useApiEnvironment,
} from "@mirage/service-auth/useApiEnvironment";
import { useIsDropboxer } from "@mirage/service-auth/useDropboxAccount";
import { listenForClientKeyOverride } from "@mirage/service-experimentation";
import { useFeatureFlags } from "@mirage/service-experimentation/useInitFeatureFlags";
import { listenForFeatureRingSettings } from "@mirage/service-feature-ring-settings";
import { tagged } from "@mirage/service-logging";
import { listen as listenForSettings } from "@mirage/service-settings";
import { useEffect, useMemo, useState } from "react";

const logger = tagged("useDevOverrideReasons");

/**
 * Returns a list of active dev override reasons. These include:
 * - A growthbook client key/ID override (e.g. devbox vs prod)
 * - A feature ring override
 * - An individual feature flag override
 * - DevTools are enabled in settings
 */
export function useDevOverrideReasons(): string[] {
  const isDropboxer = useIsDropboxer();

  // Check for a client key override
  const [clientKeyOverride, setClientKeyOverrideState] = useState<
    string | undefined
  >(undefined);
  useEffect(() => {
    listenForClientKeyOverride().subscribe({
      next: (key) => {
        logger.debug("Loaded client key override:", key);
        setClientKeyOverrideState(key);
      },
      error: (error) => {
        logger.error("Failed to load client key override:", error);
      },
    });
  }, []);

  // Check if feature ring is overridden
  const [isFeatureRingOverridden, setIsFeatureRingOverridden] = useState(false);
  useEffect(() => {
    listenForFeatureRingSettings().subscribe({
      next: (featureRingSettings) => {
        logger.debug(
          "Feature Ring override updated: ",
          featureRingSettings?.override,
        );
        const isOverridden =
          featureRingSettings?.override != null &&
          featureRingSettings?.override?.id !==
            featureRingSettings?.default?.id;
        setIsFeatureRingOverridden(isOverridden);
      },
      error: (error) => {
        logger.error("Failed to listen for feature ring settings:", error);
      },
    });
  }, []);

  // Check if any individual feature flag has an override
  const featureFlags = useFeatureFlags();
  const flagOverrides = useMemo(
    () =>
      Object.entries(featureFlags)
        .filter(([, flag]) => flag.overrideValue !== undefined)
        .map(([name]) => name),
    [featureFlags],
  );

  // check if devtools are enabled
  const [isDevToolsEnabled, setIsDevToolsEnabled] = useState(false);
  useEffect(() => {
    listenForSettings(["isDevTools"]).subscribe(({ isDevTools }) => {
      logger.info("isDevTools changed to: ", isDevTools);
      setIsDevToolsEnabled(!!isDevTools);
    });
  }, []);

  // Hook for API environment override detection
  const [apiEnv] = useApiEnvironment();
  const isApiEnvOverridden =
    apiEnv != null && apiEnv !== defaultApiEnvForThisEnv();

  // always return empty reasons when running in meticulous so we don't change basically every test when merging this
  if (
    (window as Window & { Meticulous?: { isRunningAsTest?: boolean } })
      .Meticulous?.isRunningAsTest
  ) {
    logger.debug("Meticulous test mode detected, returning empty reasons");
    return [];
  }

  if (!isDropboxer) {
    logger.debug("Not a Dropboxer, returning empty reasons");
    return [];
  }

  const reasons: string[] = [];
  if (clientKeyOverride !== undefined) {
    reasons.push("client key override");
  }
  if (isFeatureRingOverridden) {
    reasons.push("feature ring override");
  }
  flagOverrides.forEach((name) => reasons.push(`flag override: ${name}`));
  if (isDevToolsEnabled) {
    reasons.push("dev tools enabled");
  }
  // Include API environment override reason
  if (isApiEnvOverridden) {
    reasons.push(`api environment override: ${apiEnv}`);
  }

  return reasons;
}
